import React, { useEffect, useId, useMemo, useState } from 'react'
import BrandLogo from '../../components/Brand/BrandLogo'
import { brand } from '../../config/settings/brand'
import { formatCurrency } from '../../config/resources/utils'
import {
  PreviewShell,
  PreviewChrome,
  PreviewDot,
  PreviewBody,
  PreviewSidebar,
  PreviewBrand,
  PreviewBrandName,
  PreviewNav,
  PreviewNavLabel,
  PreviewNavList,
  PreviewSidebarItem,
  PreviewMenuIcon,
  PreviewHelpSection,
  PreviewMain,
  PreviewHeroCard,
  PreviewHeroTitle,
  PreviewAmount,
  PreviewSupport,
  PreviewMetaRow,
  PreviewPrevious,
  PreviewTrend,
  PreviewFootnote,
  PreviewSectionLabel,
  PreviewChartCard,
  PreviewChartSubtitle,
  PreviewAreaChart,
  PreviewMethodToggle,
  PreviewMethodButton,
  PreviewLiveChip,
  PreviewHistoryList,
  PreviewHistoryRow,
  PreviewHistoryStatus,
  PreviewConfigCard,
  PreviewConfigRow,
  PreviewConfigLabel,
  PreviewConfigValue,
  PreviewSceneFade,
} from './LandingProductPreview.styles'

const METHOD = {
  UTM: 'UTM',
  IMM: 'IMM',
}

const SAMPLE_BY_METHOD = {
  [METHOD.UTM]: {
    amount: 428350,
    previous: 412100,
    factorLabel: '2,05 UTM',
    footnote: 'Según tu resolución en UTM del SII.',
  },
  [METHOD.IMM]: {
    amount: 221421,
    previous: 210000,
    factorLabel: '40% del IMM',
    footnote: 'Según tu resolución como % del ingreso mínimo (IMM).',
  },
}

const HISTORY_ROWS = [
  { period: 'Agosto 2026', amount: 428350, status: 'pendiente' },
  { period: 'Julio 2026', amount: 412100, status: 'pagado' },
  { period: 'Junio 2026', amount: 405800, status: 'pagado' },
  { period: 'Mayo 2026', amount: 398200, status: 'pagado' },
]

const MAIN_MENU = [
  { name: 'Inicio', icon: 'las la-home', scene: 'amount' },
  { name: 'Mis Pensiones', icon: 'las la-calculator', scene: 'history' },
  { name: 'Preferencias', icon: 'las la-sliders-h', scene: 'config' },
]

const HELP_MENU = [{ name: 'Contacto', icon: 'las la-life-ring' }]

const CHART_VALUES = [42, 48, 45, 55, 58, 62, 60, 72, 70, 78, 76, 88]
const CHART_W = 320
const CHART_H = 72
const CHART_PAD_X = 4
const CHART_PAD_Y = 6

const buildAreaPath = (values) => {
  const max = Math.max(...values)
  const min = Math.min(...values)
  const range = Math.max(max - min, 1)
  const innerW = CHART_W - CHART_PAD_X * 2
  const innerH = CHART_H - CHART_PAD_Y * 2
  const step = values.length > 1 ? innerW / (values.length - 1) : 0

  const points = values.map((value, index) => {
    const x = CHART_PAD_X + index * step
    const y = CHART_PAD_Y + innerH - ((value - min) / range) * innerH
    return { x, y }
  })

  const line = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
  const last = points[points.length - 1]
  const first = points[0]
  const area = `${line} L ${last.x} ${CHART_H} L ${first.x} ${CHART_H} Z`

  return { line, area, points }
}

const { line: chartLine, area: chartArea, points: chartPoints } =
  buildAreaPath(CHART_VALUES)
const lastPoint = chartPoints[chartPoints.length - 1]

const useCountUp = (target, enabled) => {
  const [value, setValue] = useState(enabled ? 0 : target)

  useEffect(() => {
    if (!enabled) {
      setValue(target)
      return undefined
    }

    let frame = 0
    const duration = 900
    const start = performance.now()
    const from = Math.round(target * 0.82)

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration)
      const eased = 1 - (1 - progress) ** 3
      setValue(Math.round(from + (target - from) * eased))
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      }
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, enabled])

  return value
}

/**
 * Product mock for landing / auth.
 * @param {'amount'|'history'|'config'} [scene]
 * @param {boolean} [interactive] — UTM/IMM toggle + count-up (hero)
 */
const LandingProductPreview = ({
  className,
  standalone = false,
  scene: sceneProp,
  interactive = false,
}) => {
  const gradientId = `landingPensionAreaFill-${useId().replace(/:/g, '')}`
  const [method, setMethod] = useState(METHOD.UTM)
  const [internalScene, setInternalScene] = useState('amount')
  const scene = sceneProp || internalScene
  const sample = SAMPLE_BY_METHOD[method]
  const animatedAmount = useCountUp(sample.amount, interactive)
  const displayAmount = interactive ? animatedAmount : sample.amount
  const diff = sample.amount - sample.previous

  const monthLabel = useMemo(() => {
    const now = new Date()
    return now.toLocaleDateString('es-CL', { month: 'long' })
  }, [])

  const menuItems = MAIN_MENU.map((item) => ({
    ...item,
    active: item.scene === scene,
  }))

  const handleMenuClick = (nextScene) => {
    if (sceneProp) return
    setInternalScene(nextScene)
  }

  return (
    <PreviewShell
      className={className}
      $standalone={standalone}
      $interactive={interactive}
      aria-hidden={interactive ? undefined : true}
    >
      <PreviewChrome>
        <PreviewDot />
        <PreviewDot />
        <PreviewDot />
      </PreviewChrome>
      <PreviewBody $standalone={standalone}>
        <PreviewSidebar>
          <PreviewBrand>
            <BrandLogo size={18} />
            <PreviewBrandName>{brand.name}</PreviewBrandName>
          </PreviewBrand>

          <PreviewNav>
            <PreviewNavLabel>Menú</PreviewNavLabel>
            <PreviewNavList>
              {menuItems.map((item) => (
                <PreviewSidebarItem
                  key={item.name}
                  as={interactive && !sceneProp ? 'button' : 'span'}
                  type={interactive && !sceneProp ? 'button' : undefined}
                  $active={item.active}
                  onClick={
                    interactive && !sceneProp
                      ? () => handleMenuClick(item.scene)
                      : undefined
                  }
                >
                  <PreviewMenuIcon $active={item.active}>
                    <span className={item.icon} />
                  </PreviewMenuIcon>
                  {item.name}
                </PreviewSidebarItem>
              ))}
            </PreviewNavList>
          </PreviewNav>

          <PreviewHelpSection>
            <PreviewNavLabel>Ayuda</PreviewNavLabel>
            <PreviewNavList>
              {HELP_MENU.map((item) => (
                <PreviewSidebarItem key={item.name} as="span">
                  <PreviewMenuIcon>
                    <span className={item.icon} />
                  </PreviewMenuIcon>
                  {item.name}
                </PreviewSidebarItem>
              ))}
            </PreviewNavList>
          </PreviewHelpSection>
        </PreviewSidebar>

        <PreviewMain>
          <PreviewSceneFade key={scene}>
            {scene === 'amount' && (
              <>
                {interactive ? (
                  <PreviewMethodToggle role="group" aria-label="Método de cálculo">
                    <PreviewMethodButton
                      type="button"
                      $active={method === METHOD.UTM}
                      onClick={() => setMethod(METHOD.UTM)}
                    >
                      UTM
                    </PreviewMethodButton>
                    <PreviewMethodButton
                      type="button"
                      $active={method === METHOD.IMM}
                      onClick={() => setMethod(METHOD.IMM)}
                    >
                      IMM
                    </PreviewMethodButton>
                  </PreviewMethodToggle>
                ) : null}

                <PreviewHeroCard>
                  <PreviewLiveChip>
                    <span />
                    {method} vigente · {sample.factorLabel}
                  </PreviewLiveChip>
                  <PreviewHeroTitle>
                    Tu pensión de {monthLabel} ya está lista
                  </PreviewHeroTitle>
                  <PreviewAmount key={method}>
                    {formatCurrency(displayAmount)}
                  </PreviewAmount>
                  <PreviewSupport>
                    Monto estimado para este mes, listo cuando llegue el día de
                    pago.
                  </PreviewSupport>
                  <PreviewMetaRow>
                    <PreviewPrevious>
                      El mes pasado fue {formatCurrency(sample.previous)}
                    </PreviewPrevious>
                    <PreviewTrend>
                      <span className="las la-arrow-up" aria-hidden="true" />
                      Subió {formatCurrency(diff)} respecto al mes anterior
                    </PreviewTrend>
                  </PreviewMetaRow>
                  <PreviewFootnote>{sample.footnote}</PreviewFootnote>
                </PreviewHeroCard>

                <PreviewSectionLabel>Historial de pensión</PreviewSectionLabel>
                <PreviewChartCard>
                  <PreviewChartSubtitle>
                    Últimos 12 meses · montos en CLP
                  </PreviewChartSubtitle>
                  <PreviewAreaChart
                    viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient
                        id={gradientId}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3ECF8E"
                          stopOpacity="0.28"
                        />
                        <stop
                          offset="100%"
                          stopColor="#3ECF8E"
                          stopOpacity="0.02"
                        />
                      </linearGradient>
                    </defs>
                    <line
                      x1={CHART_PAD_X}
                      x2={CHART_W - CHART_PAD_X}
                      y1={CHART_H * 0.28}
                      y2={CHART_H * 0.28}
                      stroke="currentColor"
                      strokeDasharray="3 3"
                      className="grid"
                    />
                    <line
                      x1={CHART_PAD_X}
                      x2={CHART_W - CHART_PAD_X}
                      y1={CHART_H * 0.55}
                      y2={CHART_H * 0.55}
                      stroke="currentColor"
                      strokeDasharray="3 3"
                      className="grid"
                    />
                    <path
                      d={chartArea}
                      fill={`url(#${gradientId})`}
                      className="chart-area"
                    />
                    <path
                      d={chartLine}
                      fill="none"
                      stroke="#3ECF8E"
                      strokeWidth="2.25"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      className="chart-line"
                    />
                    <circle
                      cx={lastPoint.x}
                      cy={lastPoint.y}
                      r="4"
                      fill="#3ECF8E"
                      className="chart-dot"
                    />
                  </PreviewAreaChart>
                </PreviewChartCard>
              </>
            )}

            {scene === 'history' && (
              <>
                <PreviewHeroTitle>Historial por período</PreviewHeroTitle>
                <PreviewSupport>
                  Pendiente, pagado y evolución mes a mes en un solo lugar.
                </PreviewSupport>
                <PreviewHistoryList>
                  {HISTORY_ROWS.map((row) => (
                    <PreviewHistoryRow key={row.period}>
                      <div>
                        <strong>{row.period}</strong>
                        <span>{formatCurrency(row.amount)}</span>
                      </div>
                      <PreviewHistoryStatus $status={row.status}>
                        {row.status === 'pagado' ? 'Pagado' : 'Pendiente'}
                      </PreviewHistoryStatus>
                    </PreviewHistoryRow>
                  ))}
                </PreviewHistoryList>
              </>
            )}

            {scene === 'config' && (
              <>
                <PreviewHeroTitle>Preferencias de tu resolución</PreviewHeroTitle>
                <PreviewSupport>
                  Configura el método que fijó el Juzgado de Familia.
                </PreviewSupport>
                <PreviewConfigCard>
                  <PreviewConfigRow>
                    <PreviewConfigLabel>Método</PreviewConfigLabel>
                    <PreviewConfigValue>UTM · SII</PreviewConfigValue>
                  </PreviewConfigRow>
                  <PreviewConfigRow>
                    <PreviewConfigLabel>Cantidad de UTM</PreviewConfigLabel>
                    <PreviewConfigValue>2,05</PreviewConfigValue>
                  </PreviewConfigRow>
                  <PreviewConfigRow>
                    <PreviewConfigLabel>Día de pago</PreviewConfigLabel>
                    <PreviewConfigValue>Cada día 5</PreviewConfigValue>
                  </PreviewConfigRow>
                  <PreviewConfigRow>
                    <PreviewConfigLabel>Alternativa</PreviewConfigLabel>
                    <PreviewConfigValue>IMM · 40%</PreviewConfigValue>
                  </PreviewConfigRow>
                </PreviewConfigCard>
              </>
            )}
          </PreviewSceneFade>
        </PreviewMain>
      </PreviewBody>
    </PreviewShell>
  )
}

export default LandingProductPreview
